<template>
  <header class="portal-header">
    <div class="portal-header__inner">
      <router-link class="portal-brand" :to="{ name: 'HomePage' }" aria-label="UCARI Partner Portal home">
        <img src="/images/ucari-logo-v3.png" alt="UCARI" />
        <span>Partner Portal</span>
      </router-link>

      <nav class="portal-nav" aria-label="Primary navigation" v-if="isAuthChecked">
        <template v-if="isAuthenticated">
          <router-link :to="{ name: 'Shop' }" :class="{ 'is-active': isActive('Shop') }">Shop</router-link>
          <router-link :to="{ name: 'Orders' }" :class="{ 'is-active': isActive('Orders') }">Orders</router-link>
        </template>
        <template v-else>
          <router-link :to="{ name: 'HomePage' }" :class="{ 'is-active': isActive('HomePage') }">Partner program</router-link>
          <a href="mailto:care@ucari.com">Support</a>
        </template>
      </nav>

      <div class="portal-header__actions" v-if="isAuthChecked">
        <template v-if="isAuthenticated">
          <button class="header-icon-button cart-button" type="button" @click.stop="openCart" aria-label="Open cart">
            <span uk-icon="icon: cart"></span>
            <span v-if="cartCount" class="cart-badge">{{ cartCount }}</span>
          </button>

          <button class="header-icon-button" type="button" aria-label="Open account menu">
            <span uk-icon="icon: user"></span>
          </button>
          <div uk-dropdown="mode: click; pos: bottom-right" class="portal-account-menu">
            <p class="portal-account-menu__label">Partner account</p>
            <ul class="uk-nav uk-dropdown-nav">
              <li><router-link :to="{ name: 'Orders' }"><span uk-icon="icon: list"></span> My orders</router-link></li>
              <li><router-link :to="{ name: 'RequestPasswordReset' }"><span uk-icon="icon: lock"></span> Change password</router-link></li>
              <li class="uk-nav-divider"></li>
              <li><a href="#" @click.prevent="logout"><span uk-icon="icon: sign-out"></span> Sign out</a></li>
            </ul>
          </div>
        </template>

        <template v-else-if="hasCredentials">
          <button type="button" class="uk-button uk-button-default uk-button-small" @click="logout">Sign out</button>
        </template>

        <template v-else>
          <router-link v-if="!['LoginUser', 'RetailTerms'].includes($route.name)" :to="{ name: 'LoginUser' }" class="header-sign-in">Sign in</router-link>
          <router-link v-if="$route.name !== 'RegisterUser'" :to="{ name: 'RegisterUser' }" class="uk-button uk-button-primary uk-button-small header-create-account">Create account</router-link>
        </template>

        <button class="portal-menu-toggle" type="button" :aria-expanded="mobileOpen" aria-controls="portal-mobile-nav" @click="mobileOpen = !mobileOpen">
          <span></span><span></span><span></span>
          <span class="visually-hidden">Toggle navigation</span>
        </button>
      </div>
    </div>

    <transition name="drawer-fade">
      <div v-if="mobileOpen" id="portal-mobile-nav" class="portal-mobile-nav">
        <button class="portal-mobile-nav__backdrop" type="button" aria-label="Close navigation" @click="mobileOpen = false"></button>
        <div class="portal-mobile-nav__panel">
          <div class="portal-mobile-nav__head">
            <span>Menu</span>
            <button type="button" @click="mobileOpen = false" aria-label="Close navigation" uk-close></button>
          </div>
          <nav aria-label="Mobile navigation" @click="mobileOpen = false">
            <template v-if="isAuthenticated">
              <router-link :to="{ name: 'Shop' }">Shop</router-link>
              <router-link :to="{ name: 'Orders' }">My orders</router-link>
              <button type="button" @click.stop="openMobileCart">Cart <span>{{ cartCount }} kits</span></button>
              <router-link :to="{ name: 'RequestPasswordReset' }">Change password</router-link>
              <button type="button" @click.stop="logout">Sign out</button>
            </template>
            <template v-else>
              <router-link :to="{ name: 'HomePage' }">Partner program</router-link>
              <router-link :to="{ name: 'LoginUser' }">Sign in</router-link>
              <router-link :to="{ name: 'RegisterUser' }">Create account</router-link>
              <a href="mailto:care@ucari.com">Support</a>
            </template>
          </nav>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import axios from 'axios';
import { totalCartKits } from '@/utils/cart';

export default {
  name: "RetailerHeader",
  data() {
    return {
      // auth
      isAuthenticated: false,
      isAuthChecked: false,
      hasCredentials: false,
      accessToken: '',
      cid: '',

      mobileOpen: false,

      // cart
      cart: [],
      cartOpen: false,
    };
  },
  computed: {
    cartCount() {
      return totalCartKits(this.cart);
    },
    cartTotalCents() {
      return this.cart.reduce((n, i) => n + Number(i.price || 0) * Number(i.quantity || 0), 0);
    }
  },
  async mounted() {
    // Load creds + optimistic auth
    this.loadCredentials();
    this.hasCredentials = !!this.accessToken && !!this.cid;
    this.isAuthenticated = this.hasCredentials;

    // Confirm auth with backend
    await this.checkAuthentication();

    // Cart only really matters when logged in (optional)
    this.loadCart();
    this.cart = this.cart.map(i => ({
      ...i,
      quantity: this.normalizeQuantity(i.quantity || 1)
    }));

    // Keep in sync across tabs
    window.addEventListener('storage', this.onStorage);

    // Also keep in sync within this tab via custom event
    window.addEventListener('ucari:cart-updated', () => this.loadCart());

    // Event-based API (from other components)
    window.addEventListener('ucari:cart-add', (e) => {
      this.loadCart();
      this.addItem(e.detail);
    });

    window.addEventListener('ucari:cart-update', (e) => {
      this.loadCart();
      const d = e.detail;
      const patch = typeof d === 'string' ? { key: d } : d;
      this.updateItem(patch);
    });

    window.addEventListener('ucari:cart-remove', (e) => {
      this.loadCart();
      const d = e.detail;
      const key = typeof d === 'string' ? d : d?.key;
      if (key) this.removeByKey(key);
    });

    window.addEventListener('ucari:cart-clear', () => {
      this.clearCart();
    });

    // Minimal global API
    window.ucariCart = {
      get:      () => [...this.cart],
      count:    () => this.cartCount,
      total:    () => this.cartTotalCents,
      add:      (item) => { this.loadCart(); this.addItem(item); },
      update:   (patch) => { this.loadCart(); this.updateItem(patch); },
      remove:   (key) => { this.loadCart(); this.removeByKey(key); },
      clear:    () => this.clearCart(),
      open:     () => this.openCart(),
      close:    () => this.closeCart(),
      goToCheckout: () => this.goToCheckout(),
    };
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.onStorage);
    delete window.ucariCart;
  },
  methods: {
    loadCredentials() {
      this.accessToken = localStorage.getItem('accessToken') || '';
      this.cid = localStorage.getItem('cid') || '';
    },

    packSizeFromVariationName(name = '') {
      const match = String(name).match(/(\d+)/);
      const count = match ? parseInt(match[1], 10) : 1;
      return Number.isFinite(count) && count > 0 ? count : 1;
    },

    totalCartKits(cart = this.cart) {
      return cart.reduce((sum, i) => {
        const packSize = i.packSize || this.packSizeFromVariationName(i.variationName || '');
        const packQty = Number(i.quantity || 0);
        return sum + (packSize * packQty);
      }, 0);
    },

    async checkAuthentication() {
      try {
        // If you don't have creds, you're logged out
        if (!this.accessToken || !this.cid) {
          this.isAuthenticated = false;
          return;
        }

        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/check-partner`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
              cid: this.cid,
            }
          }
        );

        this.isAuthenticated = !!response.data.authenticated;
      } catch (err) {
        console.error('Partner session check failed', err);
        this.isAuthenticated = false;
      } finally {
        this.isAuthChecked = true;
      }
    },

    async logout() {
      try {
        await axios.post(
          `${process.env.VUE_APP_API_URL}/api/logout`,
          {},
          { withCredentials: true }
        );
      } catch (err) {
        // even if server fails, we can still clear client state
        console.error('Logout failed', err);
      } finally {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('cid');
        localStorage.removeItem('squareCustomerId');
        localStorage.removeItem('userEmail');

        this.isAuthenticated = false;
        this.isAuthChecked = true;

        // optional: close cart UI
        this.closeCart();

        // send them to login
        this.$router.push({ name: 'LoginUser' });
      }
    },

    normalizeQuantity(raw) {
      let q = Number(raw);
      if (!Number.isFinite(q)) q = 1;
      q = Math.floor(q);
      if (q < 1) q = 1;
      if (q > 999) q = 999;
      return q;
    },

    isActive(name) { return this.$route.name === name; },

    openMobileCart() {
      this.mobileOpen = false;
      this.$nextTick(() => this.openCart());
    },

    openCart() {
      if (!this.isAuthenticated) return; // safety
      this.cartOpen = true;
      const oc = window.UIkit?.offcanvas?.('#cart-offcanvas');
      if (!oc) return;
      setTimeout(() => oc.show(), 0);
    },

    closeCart() {
      const oc = window.UIkit?.offcanvas?.('#cart-offcanvas');
      oc && oc.hide();
      this.cartOpen = false;
    },

    goToCheckout() {
      if (!this.isAuthenticated || !this.cart.length) return;
      this.persistCart();
      this.$router.push({ name: 'Checkout' });
    },

    loadCart() {
      try { this.cart = JSON.parse(localStorage.getItem('ucari_cart') || '[]'); }
      catch { this.cart = []; }
    },

    persistCart() {
      localStorage.setItem('ucari_cart', JSON.stringify(this.cart));
      window.dispatchEvent(new CustomEvent('ucari:cart-updated'));
    },

    onStorage(e) {
      if (e.key === 'ucari_cart') this.loadCart();
    },

    addItem({ key, productId, productName, variationId, variationName, price, currency='USD', quantity=1 }) {
      const addQty = this.normalizeQuantity(quantity);
      if (!key) key = `${productId}:${variationId || 'default'}`;
      const idx = this.cart.findIndex(i => i.key === key);

      if (idx >= 0) {
        const current = this.normalizeQuantity(this.cart[idx].quantity || 1);
        const newQty = this.normalizeQuantity(current + addQty);
        this.cart[idx].quantity = newQty;
      } else {
        this.cart.push({ key, productId, productName, variationId, variationName, price, currency, quantity: addQty });
      }
      this.persistCart();
    },

    updateItem({ key, quantity }) {
      const idx = this.cart.findIndex(i => i.key === key);
      if (idx < 0) return;
      this.cart[idx].quantity = this.normalizeQuantity(quantity);
      this.persistCart();
    },

    removeByKey(key) {
      const idx = this.cart.findIndex(i => i.key === key);
      if (idx >= 0) {
        this.cart.splice(idx, 1);
        this.persistCart();
      }
    },

    clearCart() {
      this.cart = [];
      this.persistCart();
    },
  }
};
</script>
